import { StarFour } from "@phosphor-icons/react";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type StepProgressState = "done" | "active" | "pending" | "disabled";

export type StepProgressStep = {
  id?: string;
  label: ReactNode;
  status?: StepProgressState;
  disabled?: boolean;
};

export type StepProgressCardProps = HTMLAttributes<HTMLElement> & {
  eyebrow?: ReactNode;
  title?: ReactNode;
  steps: StepProgressStep[];
  currentStep?: number;
  surface?: "card" | "plain";
  animated?: boolean;
};

function getStepState(step: StepProgressStep, index: number, currentStep: number): StepProgressState {
  if (step.disabled) return "disabled";
  if (step.status) return step.status;
  if (index < currentStep) return "done";
  if (index === currentStep) return "active";
  return "pending";
}

function StepMarker({ state, animated }: { state: StepProgressState; animated: boolean }) {
  if (state === "active") {
    return (
      <span
        className={cn(
          "bfrs-flex bfrs-size-4 bfrs-origin-center bfrs-items-center bfrs-justify-center bfrs-text-[var(--bfrs-dcl-color-step-progress-active-marker)]",
          animated && "bfrs-animate-progress-active-star"
        )}
      >
        <StarFour size={14} weight="fill" />
      </span>
    );
  }

  return (
    <span
      className={cn(
        "bfrs-block bfrs-size-2 bfrs-rounded-pill bfrs-transition-colors bfrs-duration-300",
        state === "done" && "bfrs-bg-[var(--bfrs-dcl-color-step-progress-done-marker)]",
        state === "pending" && "bfrs-bg-[var(--bfrs-dcl-color-step-progress-pending)]",
        state === "disabled" && "bfrs-bg-[var(--bfrs-dcl-color-step-progress-pending)] bfrs-opacity-60",
        animated && state === "done" && "bfrs-animate-progress-step-done"
      )}
    />
  );
}

export const StepProgressCard = forwardRef<HTMLElement, StepProgressCardProps>(
  ({ className, eyebrow = "Identity & Contact", title = "Getting your details ready…", steps, currentStep = 0, surface = "plain", animated = true, ...props }, ref) => {
    const resolvedSteps = steps.map((step, index) => ({
      ...step,
      state: getStepState(step, index, currentStep)
    }));

    return (
      <section
        ref={ref}
        className={cn(
          "bfrs-w-full bfrs-max-w-[425px] bfrs-[word-break:break-word]",
          surface === "card" && "bfrs-faded-card-border bfrs-rounded-xl bfrs-bg-[image:var(--bfrs-gradient-card)] bfrs-p-5 bfrs-shadow-card",
          className
        )}
        aria-label={typeof eyebrow === "string" ? eyebrow : "Progress"}
        {...props}
      >
        {(eyebrow || title) && (
          <div className="bfrs-mb-[21px]">
            {eyebrow && (
              <p className="bfrs-text-[10px] bfrs-font-bold bfrs-uppercase bfrs-leading-normal bfrs-tracking-[1.6px] bfrs-text-[var(--bfrs-dcl-color-step-progress-eyebrow)]">
                {eyebrow}
              </p>
            )}
            {title && <h3 className="bfrs-mt-2 bfrs-text-[24px] bfrs-font-bold bfrs-leading-normal bfrs-text-[var(--bfrs-dcl-color-step-progress-title)]">{title}</h3>}
          </div>
        )}

        <ol className="bfrs-grid bfrs-gap-0" aria-live="polite">
          {resolvedSteps.map((step, index) => {
            const nextStep = resolvedSteps[index + 1];
            const connectorState = step.state === "done" ? "done" : step.state === "active" && nextStep?.state === "done" ? "done" : "pending";
            const nextIsActive = nextStep?.state === "active";
            const isMuted = step.state === "pending" || step.state === "disabled";

            return (
              <li key={step.id ?? index} className="bfrs-grid bfrs-min-h-[35px] bfrs-grid-cols-[16px_minmax(0,1fr)] bfrs-gap-2">
                <div className={cn("bfrs-relative bfrs-flex bfrs-justify-center", step.state === "active" ? "bfrs-pt-0" : "bfrs-pt-[3px]")}>
                  <StepMarker state={step.state} animated={animated} />
                  {index < resolvedSteps.length - 1 && (
                    <span
                      className={cn(
                        "bfrs-absolute bfrs-left-1/2 bfrs-w-px -bfrs-translate-x-1/2 bfrs-overflow-hidden bfrs-bg-[var(--bfrs-dcl-color-step-progress-pending)]",
                        step.state === "active" ? "bfrs-top-[22px]" : "bfrs-top-[17px]",
                        nextIsActive ? "bfrs-h-[calc(100%-17px)]" : "bfrs-h-[calc(100%-10px)]"
                      )}
                    >
                      <span
                        className={cn(
                          "bfrs-block bfrs-h-full bfrs-w-full bfrs-bg-[var(--bfrs-dcl-color-step-progress-done-marker)] bfrs-transition-colors bfrs-duration-300",
                          connectorState === "done" ? "bfrs-opacity-100" : "bfrs-opacity-0",
                          animated && connectorState === "done" && "bfrs-animate-progress-rail-fill"
                        )}
                      />
                    </span>
                  )}
                </div>
                <div className="bfrs-pb-4">
                  <p
                    className={cn(
                      "bfrs-whitespace-normal bfrs-text-[12px] bfrs-leading-normal bfrs-transition-all bfrs-duration-300",
                      step.state === "done" && "bfrs-font-normal bfrs-text-[var(--bfrs-dcl-color-step-progress-done-text)]",
                      step.state === "active" && "bfrs-font-medium bfrs-text-[var(--bfrs-dcl-color-step-progress-active-text)]",
                      isMuted && "bfrs-font-normal bfrs-text-[var(--bfrs-dcl-color-step-progress-pending)]"
                    )}
                  >
                    {step.label}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    );
  }
);

StepProgressCard.displayName = "StepProgressCard";
